import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../utils/api";
import { Search, X, ChevronDown, Check, Building2, LoaderCircle } from "lucide-react";

const LIMIT = 100;

// The panel is portalled to <body> and positioned against the trigger rather
// than nested under it. An absolutely-positioned child cannot escape an
// ancestor's `overflow-hidden` no matter its z-index, and this picker is used
// inside SectionCards, scrolling Modal bodies and filter bars — all of which
// clip. z-[70] puts it above Modal's z-[60] so it works inside dialogs too.
const PANEL_Z = 70;
const PANEL_MIN_W = 320;
const PANEL_MAX_H = 420;
const GAP = 8;

// Where the panel sits: below the trigger, flipping above when the space
// underneath is too tight to be usable.
function panelPosition(rect) {
  const below = window.innerHeight - rect.bottom - GAP;
  const above = rect.top - GAP;
  const flip = below < 240 && above > below;
  const maxHeight = Math.min(PANEL_MAX_H, Math.max(flip ? above : below, 180));

  return {
    width: Math.max(rect.width, PANEL_MIN_W),
    left: Math.min(rect.left, window.innerWidth - Math.max(rect.width, PANEL_MIN_W) - GAP),
    top: flip ? rect.top - GAP - maxHeight : rect.bottom + GAP,
    maxHeight,
  };
}

/**
 * ClientSelectPopup — searchable, paged client picker.
 *
 * Props:
 *  - value        : string (clientId currently selected)
 *  - onChange     : (clientId, clientObject) => void
 *  - placeholder  : string  (default "Select client…")
 *  - label        : string  (optional field label above trigger)
 *  - error        : string  (optional validation message)
 *  - disabled     : bool
 */
export default function ClientSelectPopup({
  value,
  onChange,
  placeholder = "Select client…",
  label,
  error,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // What the field shows vs. what the query runs on — see handleSearch
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pos, setPos] = useState(null);
  const searchRef = useRef(null);
  const popupRef = useRef(null);
  const panelRef = useRef(null);
  const debRef = useRef(null);
  useEffect(() => () => clearTimeout(debRef.current), []);

  /* close on outside click / Esc — the panel lives outside popupRef now that
     it's portalled, so both subtrees count as "inside" */
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popupRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  /* keep the panel glued to the trigger — capture:true catches scrolling in
     any ancestor scroller, not just the window */
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }

    const reposition = () => {
      const trigger = popupRef.current;
      if (trigger) setPos(panelPosition(trigger.getBoundingClientRect()));
    };
    reposition();

    /* An on-screen keyboard opening or closing fires `resize` with the height
       changed and the width untouched. Repositioning on that shrinks the space
       below the trigger, flips the panel above it and yanks the field out from
       under the user's thumb mid-word — so while focus is inside the panel,
       only width changes (a real rotation or window resize) count. */
    let lastWidth = window.innerWidth;
    const onResize = () => {
      const width = window.innerWidth;
      const keyboardOnly = width === lastWidth && panelRef.current?.contains(document.activeElement);
      lastWidth = width;
      if (!keyboardOnly) reposition();
    };

    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => searchRef.current?.focus(), 60); }, [open]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (search) p.set("search", search);
      const { data } = await api.get(`/clients?${p}`);
      const list = data.data.clients || data.data || [];
      setClients(list);
      setTotal(data.data.total ?? list.length);
      setPages(data.data.pages ?? 1);
    } catch { setClients([]); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { if (open) fetchClients(); }, [open, fetchClients]);

  /* The field updates on every keystroke; the query trails it. Driving `search`
     directly fired a request per character, and each one flipped the list into
     its loading state under the user's fingers. */
  const handleSearch = (v) => {
    setSearchInput(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 350);
  };

  /* resolve the display name for the current value */
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    const found = clients.find((c) => c._id === value);
    if (found) { setSelected(found); return; }
    if (!open) {
      api.get(`/clients/${value}`)
        .then(({ data }) => setSelected(data.data || data))
        .catch(() => setSelected(null));
    }
  }, [value, clients, open]);

  const pick = (client) => {
    onChange(client._id, client);
    setSelected(client);
    setOpen(false);
    clearTimeout(debRef.current);   // a pending debounce would refill the query
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("", null);
    setSelected(null);
  };

  const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  // Stable colour per client so the same avatar always looks the same
  const hue = (str = "") => {
    const colors = [
      "bg-violet-100 text-violet-700", "bg-sky-100 text-sky-700",
      "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700",
      "bg-rose-100 text-rose-700", "bg-indigo-100 text-indigo-700",
    ];
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
    return colors[h % colors.length];
  };

  return (
    <div className="relative flex flex-col gap-1.5" ref={popupRef}>
      {label && <label className="text-xs font-semibold text-ink-600 tracking-wide">{label}</label>}

      {/* ── trigger ── */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center gap-2.5 pl-2.5 pr-3 py-2 rounded-xl border text-sm text-left min-h-[46px]
          transition-all focus:outline-none focus:ring-4 focus:ring-brand-100
          ${disabled ? "opacity-60 cursor-not-allowed bg-ink-50 border-ink-200" : "bg-white border-ink-300 hover:border-ink-400 cursor-pointer"}
          ${open ? "border-brand-500 ring-4 ring-brand-100" : ""}
          ${error ? "border-red-400" : ""}`}
      >
        {selected ? (
          <>
            <span className={`w-8 h-8 rounded-lg grid place-items-center text-[11px] font-bold shrink-0 ${hue(selected.name)}`}>
              {initials(selected.name)}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-ink-900 truncate">{selected.name}</span>
              {selected.company && <span className="block text-xs text-ink-400 truncate">{selected.company}</span>}
            </span>
            {!disabled && (
              <span role="button" tabIndex={0} onClick={clear} onKeyDown={(e) => e.key === "Enter" && clear(e)}
                aria-label="Clear selection"
                className="w-6 h-6 grid place-items-center rounded-full text-ink-400 hover:text-ink-700 hover:bg-ink-100 shrink-0 transition-colors">
                <X size={13} />
              </span>
            )}
          </>
        ) : (
          <>
            <span className="w-8 h-8 rounded-lg bg-ink-100 grid place-items-center text-ink-400 shrink-0">
              <Building2 size={15} />
            </span>
            <span className="flex-1 text-ink-400 truncate">{placeholder}</span>
            <ChevronDown size={15} className="text-ink-400 shrink-0" />
          </>
        )}
      </button>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* ── panel ── portalled to <body> so no ancestor can clip it ── */}
      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed",
            top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight,
            zIndex: PANEL_Z,
          }}
          className="flex flex-col bg-white border border-ink-200 rounded-2xl shadow-pop overflow-hidden animate-fade-up"
        >

          <div className="p-2.5 border-b border-ink-100 shrink-0">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
              <input
                ref={searchRef} type="text" value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by name or company…"
                className="w-full pl-9 pr-8 py-2.5 text-sm bg-ink-50 border border-ink-200 rounded-xl
                           focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-400 focus:bg-white
                           placeholder:text-ink-400 transition-all"
              />
              {searchInput && (
                <button type="button" onClick={() => handleSearch("")} aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 grid place-items-center rounded-full text-ink-400 hover:text-ink-700 hover:bg-ink-200">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="px-3 py-1.5 text-[11px] text-ink-400 border-b border-ink-50 bg-ink-50/50 shrink-0">
            {loading ? "Searching…" : `${total} client${total !== 1 ? "s" : ""}${search ? ` matching “${search}”` : ""}`}
          </div>

          {/* the list absorbs whatever height is left after the chrome */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain divide-y divide-ink-50" role="listbox">
            {loading ? (
              <div className="py-10 flex items-center justify-center gap-2 text-sm text-ink-400">
                <LoaderCircle size={15} className="animate-spin" /> Loading…
              </div>
            ) : clients.length === 0 ? (
              <div className="py-10 text-center text-sm text-ink-400">
                {search ? `No clients found for “${search}”` : "No clients available"}
              </div>
            ) : (
              clients.map((client) => {
                const isActive = client._id === value;
                return (
                  <button key={client._id} type="button" role="option" aria-selected={isActive}
                    onClick={() => pick(client)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isActive ? "bg-brand-50" : "hover:bg-ink-50"}`}>
                    <span className={`w-8 h-8 rounded-lg grid place-items-center text-[11px] font-bold shrink-0 ${hue(client.name)}`}>
                      {initials(client.name)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm font-medium truncate ${isActive ? "text-brand-700" : "text-ink-800"}`}>{client.name}</span>
                      {(client.company || client.email) && (
                        <span className="block text-xs text-ink-400 truncate">{[client.company, client.email].filter(Boolean).join(" · ")}</span>
                      )}
                    </span>
                    {isActive && <Check size={15} className="text-brand-600 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-ink-100 bg-ink-50/70 shrink-0">
              <span className="text-[11px] text-ink-400">Page {page} of {pages}</span>
              <div className="flex gap-1">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  className="px-2.5 py-1.5 text-xs rounded-lg border border-ink-200 bg-white text-ink-600 hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Prev
                </button>
                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                  className="px-2.5 py-1.5 text-xs rounded-lg border border-ink-200 bg-white text-ink-600 hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Next
                </button>
              </div>
            </div>
          )}

          <div className="border-t border-ink-100 shrink-0">
            <button type="button"
              onClick={() => { onChange("", null); setSelected(null); setOpen(false); }}
              className="w-full px-3 py-2.5 text-left text-sm text-ink-400 hover:bg-ink-50 transition-colors flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-ink-100 grid place-items-center text-ink-300 shrink-0">—</span>
              No client
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
